import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Campaign, CampaignHistory as HistoryRow } from "../api/types";
import { campaignPath } from "../lib/campaign";
import { shortDate } from "../lib/format";
import { useListParams } from "../lib/useListParams";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { DownloadJobFile } from "./DownloadJobFile";
import { Spinner } from "./Spinner";

/* Everything that happened to a campaign, newest first, in one table: made,
 * every stage move, renames, criteria and call notes, automatic web lookups,
 * archive and restore, manual enrichment, each change of owner, each list
 * taken, each export with its file, and each failure. Each export downloads
 * from its row, and each list opens.
 *
 * One read of api.campaign_history (engine migrations 215 and 219), which
 * also writes each row's words and orders the rows. It replaced four reads
 * stitched together here, and the lake now keeps every stage move rather
 * than the latest. The note under an event is the engine's: the enricher's
 * note, a failure's reason, or how many people an export left out for
 * another owner. On the data table, so a phone gets one row per event
 * rather than a table wider than the screen.
 * docs/plans/campaign-assignment.md, milestone 8. */
const LIMIT = 500;

export function CampaignHistory({ token, c }: { token: string; c: Campaign }) {
  const name = c.name ?? "";
  // The engine orders the rows, so no column sorts.
  const params = useListParams("at", [], false, "", "ch.");
  const q = useQuery({
    queryKey: ["campaign-history", name],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_history")
        .select("at,kind,what,by_name,rows,list_id,job_id,has_file,failed,note,seq")
        .eq("campaign", name)
        .order("at", { ascending: false }).order("seq", { ascending: false, nullsFirst: false })
        .limit(LIMIT);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as HistoryRow[];
    },
  });

  if (q.isLoading) return <section className="rp-tabbody"><p className="muted"><Spinner />Loading.</p></section>;
  if (q.isError) return <section className="rp-tabbody"><p className="refusal" role="alert">{(q.error as Error).message}</p></section>;

  const rows = q.data ?? [];
  if (!rows.length) {
    return <section className="rp-tabbody" aria-label="History"><p className="muted">No history yet.</p></section>;
  }
  const listLink = (id: string | null | undefined) => !id ? null
    : id === c.list_id ? <span className="muted">Current list</span>
    : <Link to={`${campaignPath(name)}?list=${id}`}>Open this list</Link>;

  const columns: ColumnSpec<HistoryRow>[] = [
    { id: "when", header: "When", role: "leading", fields: [], narrowLabel: null,
      cell: (e) => <span className="ch-when">{shortDate(e.at)}</span> },
    { id: "what", header: "What", role: "leading", fields: [], title: true, narrowLabel: null,
      cell: (e) => (
        <>
          <span className={e.failed ? "refusal" : undefined}>{e.what}</span>
          {e.note ? <span className={e.failed ? "refusal ch-note" : "muted ch-note"}>{e.kind === "manual" ? `"${e.note}"` : e.note}</span> : null}
        </>
      ) },
    { id: "by", header: "By", role: "secondary", fields: [], cell: (e) => e.by_name ?? "" },
    { id: "rows", header: "Rows", role: "secondary", fields: [], align: "num", narrowLabel: null,
      cell: (e) => e.rows === null || e.rows === undefined ? "" : `${e.rows.toLocaleString()} properties` },
    { id: "file", header: "File", role: "secondary", fields: [], narrowLabel: null,
      cell: (e) => (
        <span className="ch-files">
          {e.has_file && e.job_id ? <DownloadJobFile token={token} jobId={e.job_id} />
            : e.kind === "export" ? <span className="muted">No file to download</span> : null}
          {listLink(e.list_id)}
        </span>
      ) },
  ];

  return (
    <section className="rp-tabbody" aria-label="History">
      <DataTable columns={columns} rows={rows} params={params}
                 rowKey={(e) => `${e.at}|${e.kind}|${e.seq ?? e.job_id ?? e.list_id ?? e.what}`} />
      {rows.length >= LIMIT ? <p className="muted rp-foot">Showing the newest {LIMIT.toLocaleString()} events.</p> : null}
    </section>
  );
}
